/**
 * Tab-target locking: the rules both sides agree on.
 *
 * WHY THE GAME HAS A LOCK AT ALL. STILLSUN's camera is a third-person boom the player spins
 * with the right mouse button, and the body underneath it turns with A/D. Neither is an aim.
 * A crosshair over a shoulder is not a promise the engine can keep at 300 characters in a
 * city, and the first thing a player asks of an MMORPG is "hit *that* one".
 *
 * So aiming is a *selection*, not a direction. You pick a body — by clicking it, or by
 * walking Tab through what is near — the character turns to it, and everything targeted
 * resolves against it. Area abilities (a cleave, a ward) keep their own arc test; the arc
 * stops being the thing that decides whether an ordinary swing connects.
 *
 * This file holds only the parts the client and the server must agree about: how far a lock
 * may be acquired, how far it may be held, and the order Tab walks through candidates in.
 * Whether a particular body is a *legal* target is the server's call — the client's list is a
 * suggestion, and a suggestion the server refuses simply never comes back in the snapshot.
 *
 * Deterministic maths only (no Math.random, no trigonometry): the cycling order has to be
 * identical on both sides or Tab would select something different from what the server locks.
 *
 * Lifted from litew8 `shared/src/targeting.ts` and rescaled — STILLSUN's world is metres, not
 * litew8's arbitrary units, so the two ranges are the plan's 40 m / 50 m rather than 700/900.
 */

/**
 * How far away a lock may be ACQUIRED, in metres.
 *
 * Comfortably past every ability's reach (the longest Warden cast is well inside 30 m) so that
 * "target it, then walk into range" is the normal way a fight starts, and inside the snapshot's
 * relevance radius so a locked body is always one the client has actually been sent and can
 * therefore draw a frame for.
 */
export const TARGET_ACQUIRE_RANGE = 40;

/**
 * How far a lock may be HELD before it drops, in metres.
 *
 * Larger than the acquire range on purpose. A single radius means a target hovering on the
 * boundary is acquired, dropped, re-acquired and dropped again as it walks, and a target frame
 * that flickers is worse than no target frame. Hysteresis costs one constant and removes the
 * whole failure mode.
 */
export const TARGET_KEEP_RANGE = 50;

export const TARGET_ACQUIRE_RANGE_SQ = TARGET_ACQUIRE_RANGE * TARGET_ACQUIRE_RANGE;
export const TARGET_KEEP_RANGE_SQ = TARGET_KEEP_RANGE * TARGET_KEEP_RANGE;

/**
 * The name protocol v3 and `room.ts` use for the same number: the furthest a lock may be held,
 * and therefore the furthest an `InputCmd.targetId` the server will accept can be. Aliased
 * rather than duplicated so the client's Tab ring and the server's validator can never drift.
 */
export const TARGET_MAX_RANGE = TARGET_KEEP_RANGE;
export const TARGET_MAX_RANGE_SQ = TARGET_KEEP_RANGE_SQ;

/** One thing Tab could select: an id and how far away it is. */
export interface TargetCandidate {
  id: number;
  /** Squared distance from the player. Squared, so nothing has to take a root. */
  distSq: number;
}

/**
 * Sorts candidates into the order Tab walks: nearest first, id as the tie-break.
 *
 * The tie-break is not decoration. Two Choristers at the same distance would otherwise sort by
 * whatever order the spatial hash happened to yield, which differs between the client's entity
 * map and the server's, and Tab would select one while the server locked the other. Sorting in
 * place, and returning the same array, so the caller can keep one scratch list for the session.
 */
export function sortTargetCandidates(list: TargetCandidate[]): TargetCandidate[] {
  list.sort((a, b) => a.distSq - b.distSq || a.id - b.id);
  return list;
}

/**
 * The next id Tab should select.
 *
 * `currentId` of 0 (or an id no longer in the list — it died, it walked off) starts from the
 * nearest, which is what a player pressing Tab in a fresh fight expects. Otherwise it steps one
 * place through the sorted ring, so repeated presses walk outward and wrap; `backwards`
 * (Shift-Tab) walks in. Returns 0 when there is nothing to select, which the caller must treat
 * as "clear the lock" rather than "keep what you had": pressing Tab with nothing in range is
 * how a player drops a target they can no longer see.
 */
export function nextTargetId(
  sorted: readonly TargetCandidate[],
  currentId: number,
  backwards = false,
): number {
  if (sorted.length === 0) return 0;
  let at = -1;
  for (let i = 0; i < sorted.length; i++) {
    if (sorted[i]!.id === currentId) {
      at = i;
      break;
    }
  }
  if (at < 0) return sorted[backwards ? sorted.length - 1 : 0]!.id;
  const step = backwards ? -1 : 1;
  const next = (at + step + sorted.length) % sorted.length;
  return sorted[next]!.id;
}
